import { useRef, useCallback, useState, useEffect } from 'react';
import { Direction } from '../types';
import { AUDIO_PATHS, BACKGROUND_MUSIC } from '../constants';

/**
 * Custom hook for managing game audio
 * Plays directional sounds when tiles move and background music
 * Separate controls for music and sound effects
 */
export function useAudio() {
  const audioRefs = useRef<Record<string, HTMLAudioElement>>({});
  const musicRef = useRef<HTMLAudioElement | null>(null);
  // Use localStorage with alien2048-specific keys (synced with Settings page)
  // Settings stores "enabled" (true = on), we store "muted" (true = off), so invert
  const [isMusicMuted, setIsMusicMuted] = useState(() => localStorage.getItem('alien2048_music') === 'false');
  const [isSoundMuted, setIsSoundMuted] = useState(() => localStorage.getItem('alien2048_sfx') === 'false');

  // Randomly select background music on initialization
  const [selectedMusic] = useState(() => {
    const randomIndex = Math.floor(Math.random() * BACKGROUND_MUSIC.length);
    return BACKGROUND_MUSIC[randomIndex];
  });

  // Initialize background music
  useEffect(() => {
    musicRef.current = new Audio(selectedMusic);
    musicRef.current.loop = true;
    musicRef.current.volume = 0.2; // Lower volume for background music
  }, [selectedMusic]);

  // Initialize audio elements lazily
  const getAudio = useCallback((path: string): HTMLAudioElement => {
    if (!audioRefs.current[path]) {
      const audio = new Audio(path);
      audio.volume = 0.1; // Quieter sound effects (music is 0.2)
      audioRefs.current[path] = audio;
    }
    return audioRefs.current[path];
  }, []);

  const playSound = useCallback((path: string) => {
    if (isSoundMuted) return; // Don't play if sound effects muted

    try {
      const audio = getAudio(path);
      audio.currentTime = 0; // Reset to start
      audio.play().catch(err => {
        // Silently fail if autoplay is blocked
        console.debug('Audio play failed:', err);
      });
    } catch (err) {
      console.debug('Audio error:', err);
    }
  }, [getAudio, isSoundMuted]);

  const playMoveSound = useCallback((direction: Direction) => {
    const soundMap: Record<Direction, string> = {
      up: AUDIO_PATHS.MOVE_UP,
      down: AUDIO_PATHS.MOVE_DOWN,
      left: AUDIO_PATHS.MOVE_LEFT,
      right: AUDIO_PATHS.MOVE_RIGHT
    };

    playSound(soundMap[direction]);
  }, [playSound]);

  const playMergeSound = useCallback(() => {
    playSound(AUDIO_PATHS.MERGE);
  }, [playSound]);

  const playInvalidSound = useCallback(() => {
    playSound(AUDIO_PATHS.INVALID);
  }, [playSound]);

  const startMusic = useCallback(() => {
    if (!musicRef.current || isMusicMuted) return;

    musicRef.current.play().catch(err => {
      console.debug('Music play failed:', err);
    });
  }, [isMusicMuted]);

  const stopMusic = useCallback(() => {
    if (!musicRef.current) return;
    musicRef.current.pause();
  }, []);

  const toggleMusicMute = useCallback(() => {
    setIsMusicMuted(prev => {
      const newMuted = !prev;

      // Stop or start music based on mute state
      if (newMuted && musicRef.current) {
        musicRef.current.pause();
      } else if (!newMuted && musicRef.current && musicRef.current.paused) {
        musicRef.current.play().catch(err => {
          console.debug('Music play failed:', err);
        });
      }

      // Persist to localStorage (invert: muted=true means enabled=false)
      localStorage.setItem('alien2048_music', (!newMuted).toString());

      return newMuted;
    });
  }, []);

  const toggleSoundMute = useCallback(() => {
    setIsSoundMuted(prev => {
      const newMuted = !prev;
      // Persist to localStorage (invert: muted=true means enabled=false)
      localStorage.setItem('alien2048_sfx', (!newMuted).toString());
      return newMuted;
    });
  }, []);

  return {
    playMoveSound,
    playMergeSound,
    playInvalidSound,
    startMusic,
    stopMusic,
    isMusicMuted,
    isSoundMuted,
    toggleMusicMute,
    toggleSoundMute
  };
}
